import { Fragment, useMemo } from 'react';
import PropTypes from 'prop-types';
import Link from '@mui/material/Link';
import Pagination from '@mui/material/Pagination';
import Stack from '@mui/material/Stack';
import TableCell from '@mui/material/TableCell';
import TableFooter from '@mui/material/TableFooter';
import Typography from '@mui/material/Typography';
import { useSelector } from 'react-redux';

import { getLabelMessage } from '@gef-ui/localization/messages';

import { RESULTS_LIMIT } from 'constants/general';
import fetch from 'features/common/fetch';
import { joinString } from 'utils/string';

export const PaginationRow = ({
	fetchStorePath,
	onPaginationClick,
	onLoadNext,
	loadNextMessage = 'common:loadNext',
	hideOnResultsLimit = false,
	customResultsLimit,
	colSpan = 20,
	noTableFooter = false,
}) => {
	const metaData = useSelector(fetch.selectors.getMetaData(fetchStorePath));
	const { page: pageValue = 0, size, totalRows } = metaData;

	const currentPage = pageValue + 1;
	const count = useMemo(() => metaData && Math.ceil(metaData?.totalRows / metaData?.size), [metaData]);

	const hasMorePages = totalRows > currentPage * size;
	const show = hasMorePages || currentPage > 1;
	const hidePagination = hideOnResultsLimit && totalRows > (customResultsLimit || RESULTS_LIMIT);

	// No paginationRow if not needed / nothing to render to render or specifically set to hide
	if (!show || hidePagination) {
		return null;
	}

	const Wrapper = noTableFooter ? Fragment : TableFooter;

	return (
		<Wrapper>
			<TableCell colSpan={colSpan} sx={{ borderBottom: 'unset' }}>
				<Stack
					spacing={2}
					direction="row"
					sx={{
						justifyContent: 'space-between',
						alignItems: 'center',
					}}
				>
					<Link
						sx={{
							color: currentPage === count ? 'grey.400' : 'primary.main',
							cursor: currentPage === count ? 'default' : 'pointer',
							fontWeight: 'bold',
							fontSize: 14,
						}}
						onClick={currentPage === count ? null : onLoadNext}
						underline="none"
					>
						{getLabelMessage(loadNextMessage)}
					</Link>
					<Pagination count={count} color="primary" page={currentPage} onChange={onPaginationClick} />
					<Typography variant="subtitle2" color="grey.400">
						{joinString(' ', [getLabelMessage('common:total'), totalRows])}
					</Typography>
				</Stack>
			</TableCell>
		</Wrapper>
	);
};

PaginationRow.propTypes = {
	colSpan: PropTypes.number,
	customResultsLimit: PropTypes.number,
	fetchStorePath: PropTypes.string,
	hideOnResultsLimit: PropTypes.bool,
	loadNextMessage: PropTypes.string,
	noTableFooter: PropTypes.bool,
	onLoadNext: PropTypes.func,
	onPaginationClick: PropTypes.func,
};

export default PaginationRow;
